/**
 * WelcomeTour — dramatically simplified intro panel.
 * Desktop: elegant side panel (right). Mobile: bottom sheet.
 * First visit → auto-expanded. Dismiss → Starflower tile (bottom-right).
 * Content: page_layouts key home:welcome-tour (tour_card blocks, admin-editable).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { X, ChevronRight, ChevronLeft, ArrowRight, Headphones, Music } from "lucide-react";
import { STARFLOWER_LOGO } from "./StarPathMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "home:welcome-tour";
const SEEN_KEY = "wall_tour_seen_v1";
const absUrl = (u) => (!u || u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const lines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
/** Render [text](url) markdown links inside plain text — links open in new tabs */
const renderLinked = (text) => {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (m) return <a key={i} href={m[2]} target="_blank" rel="noopener noreferrer" className="underline text-[#F2CC8F] hover:text-white">{m[1]}</a>;
    return <span key={i}>{part}</span>;
  });
};

// openCard: a RegExp — opens the panel straight onto the first card whose title/eyebrow matches (e.g. /band/i on the musician form).
export const WelcomeTour = ({ openCard = null }) => {
  const [cards, setCards] = useState([]);
  const [open, setOpen] = useState(
    () => !!openCard || new URLSearchParams(window.location.search).get("tour") === "1" || !localStorage.getItem(SEEN_KEY)
  );
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!openCard || !cards.length) return;
    const i = cards.findIndex((c) => openCard.test(`${c.title} ${c.config?.eyebrow || ""}`));
    if (i >= 0) setActive(i);
  }, [cards, openCard]);

  useEffect(() => {
    axios.get(`${API}/page-layouts/${KEY}`)
      .then((r) => setCards((r.data.blocks || []).filter((b) => b.type === "tour_card")))
      .catch(() => {});
    const openIt = (e) => { setActive(typeof e.detail === "number" ? e.detail : null); setOpen(true); };
    window.addEventListener("wall:open-tour", openIt);
    return () => window.removeEventListener("wall:open-tour", openIt);
  }, []);

  const dismiss = useCallback(() => { localStorage.setItem(SEEN_KEY, "1"); setOpen(false); setActive(null); }, []);
  const go = useCallback((route) => {
    dismiss();
    if (!route) return;
    if (route.startsWith("http")) { window.open(route, "_blank", "noopener"); return; }
    const [path, hash] = route.split("#");
    navigate(path || "/");
    if (hash) setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
  }, [dismiss, navigate]);

  if (!cards.length) return null;

  /* ─── Collapsed: Starflower tile ─── */
  if (!open) {
    return (
      <div
        className="fixed right-4 z-[65] flex items-center gap-2 tour-tile-wrap"
        style={{ bottom: "calc(var(--dock-h, 0px) + 16px)" }}
        data-testid="tour-tile-wrap"
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-full bg-[#0F1A22] shadow-xl ring-1 ring-white/20 hover:ring-[#F2CC8F]/60 px-3 py-1.5 text-[10px] font-bold tracking-widest text-[#F2CC8F] uppercase whitespace-nowrap transition-all hover:bg-[#1a2730]"
          data-testid="tour-bubble"
        >
          The tour
        </button>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="h-12 w-12 rounded-full bg-[#0F1A22] shadow-xl ring-2 ring-[#F2CC8F]/40 hover:ring-[#F2CC8F] transition-all overflow-hidden tour-tile shrink-0"
          aria-label="Open the welcome tour"
          title="New here? Take the tour"
          data-testid="tour-tile"
        >
          <img src={STARFLOWER_LOGO} alt="Starflower" className="h-full w-full object-contain p-1.5" />
        </button>
      </div>
    );
  }

  /* ─── Expanded panel ─── */
  return (
    <>
      <div className="fixed inset-0 z-[66] bg-black/50 backdrop-blur-[3px] sm:bg-black/30 sm:backdrop-blur-[1px]" onClick={dismiss} aria-hidden="true" />
      <aside
        className="fixed z-[67] text-white bg-[#0C1117] shadow-2xl flex flex-col tour-panel
          left-0 right-0 bottom-0 max-h-[85vh] rounded-t-3xl border-t border-white/10
          sm:left-auto sm:right-0 sm:top-0 sm:bottom-0 sm:max-h-none sm:w-[380px] sm:rounded-none sm:border-l sm:border-t-0"
        role="dialog"
        aria-label="Welcome to THE WALL"
        data-testid="welcome-tour"
      >
        {/* Handle bar (mobile) */}
        <div className="sm:hidden flex justify-center pt-2 pb-0">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-3 pb-3 sm:pt-5">
          <div className="flex items-center gap-3">
            <img src={STARFLOWER_LOGO} alt="" className="h-7 w-7 object-contain opacity-80" />
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#F2CC8F]/80">New here?</p>
              <h2 className="font-['Playfair_Display'] text-base leading-tight tracking-tight">Which door is yours?</h2>
            </div>
          </div>
          <button type="button" onClick={dismiss} className="p-1.5 rounded-full hover:bg-white/10 text-white/50 hover:text-white transition-colors" aria-label="Close tour" data-testid="tour-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {active === null ? (
          <CardList cards={cards} onSelect={setActive} />
        ) : (
          <TourDetail card={cards[active]} index={active} total={cards.length} onBack={() => setActive(null)} onPrev={() => setActive((active - 1 + cards.length) % cards.length)} onNext={() => setActive((active + 1) % cards.length)} onGo={go} />
        )}
      </aside>
    </>
  );
};

/* ─── Card list ─── */
const CardList = ({ cards, onSelect }) => (
  <div className="flex-1 overflow-y-auto" data-testid="tour-cards-list">
    <ul className="px-4 pb-5 space-y-2">
      {cards.map((c, i) => {
        const cfg = c.config || {};
        const accent = cfg.accent || "#F2CC8F";
        const isMusic = cfg.is_player_tutorial;
        const isWelcome = i === 0; // first card gets expanded treatment
        return (
          <li
            key={c.block_id}
            className="tour-card"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            {isWelcome ? (
              /* ── Welcome card: full quip visible + "more" button ── */
              <div
                className="w-full text-left rounded-xl overflow-hidden border border-white/[0.08] bg-white/[0.04]"
                data-testid={`tour-card-${i}`}
              >
                {cfg.image && (
                  <div className="relative h-24 overflow-hidden">
                    <img src={absUrl(cfg.image)} alt="" className="absolute inset-0 h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0C1117] via-[#0C1117]/30 to-transparent" />
                    {cfg.eyebrow && (
                      <span className="absolute bottom-2 left-3 text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>{cfg.eyebrow}</span>
                    )}
                  </div>
                )}
                <div className="px-3 py-3">
                  <p className="font-semibold text-sm leading-snug text-white/95">{c.title}</p>
                  <p className="text-[11px] text-white/60 leading-relaxed mt-1.5">{renderLinked(c.quip)}</p>
                  <button
                    type="button"
                    onClick={() => onSelect(i)}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold hover:brightness-125 transition-colors"
                    style={{ color: accent }}
                    data-testid="tour-welcome-more"
                  >
                    More <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ) : (
              /* ── Compact card ── */
              <button
                type="button"
                onClick={() => onSelect(i)}
                className="w-full text-left group rounded-xl overflow-hidden border border-white/[0.06] hover:border-white/15 bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-200"
                data-testid={`tour-card-${i}`}
              >
                {cfg.image && (
                  <div className="relative h-20 overflow-hidden">
                    <img src={absUrl(cfg.image)} alt="" className="absolute inset-0 h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0C1117]/80 via-[#0C1117]/40 to-transparent" />
                    {cfg.eyebrow && (
                      <span className="absolute bottom-2 left-3 text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>{cfg.eyebrow}</span>
                    )}
                    {isMusic && (
                      <span className="absolute top-2 right-2 h-6 w-6 rounded-full bg-[#54E3FF]/20 flex items-center justify-center">
                        <Headphones className="h-3 w-3 text-[#54E3FF]" />
                      </span>
                    )}
                  </div>
                )}
                {!cfg.image && cfg.eyebrow && (
                  <div className="px-3 pt-2.5">
                    <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>{cfg.eyebrow}</span>
                  </div>
                )}
                <div className="px-3 py-2.5 flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm leading-snug text-white/95 group-hover:text-white">{c.title}</p>
                    <p className="text-[11px] text-white/50 line-clamp-1 mt-0.5">{c.quip}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-white/20 group-hover:text-white/50 shrink-0 transition-colors" />
                </div>
              </button>
            )}
          </li>
        );
      })}
    </ul>

    {/* Steps strip — minimal */}
    <div className="px-4 pb-4">
      <div className="flex items-center gap-2 justify-center text-[10px] text-white/35 font-medium">
        <span className="h-4 w-4 rounded-full bg-[#F2CC8F]/15 text-[#F2CC8F] text-[8px] font-black flex items-center justify-center">1</span>
        <span>Pick your door</span>
        <span className="text-white/15">→</span>
        <span className="h-4 w-4 rounded-full bg-[#F2CC8F]/15 text-[#F2CC8F] text-[8px] font-black flex items-center justify-center">2</span>
        <span>Fill one form</span>
        <span className="text-white/15">→</span>
        <span className="h-4 w-4 rounded-full bg-[#F2CC8F]/15 text-[#F2CC8F] text-[8px] font-black flex items-center justify-center">3</span>
        <span>Live today</span>
      </div>
    </div>

    <p className="text-[10px] text-white/25 text-center pb-3 px-4">
      Closing this tucks it into the Starflower, bottom-right.{" "}
      <Link to="/admin/welcome-tour" className="underline hover:text-white/40">Edit cards</Link>
    </p>
  </div>
);

/* ─── Card detail ─── */
const TourDetail = ({ card, index, total, onBack, onPrev, onNext, onGo }) => {
  const cfg = card.config || {};
  const bullets = useMemo(() => lines(cfg.bullets), [cfg.bullets]);
  const accent = cfg.accent || "#F2CC8F";

  return (
    <div className="flex-1 overflow-y-auto flex flex-col" data-testid={`tour-detail-${index}`}>
      {/* Hero image */}
      <div className="relative h-40 shrink-0 bg-[#111820]">
        {cfg.image && (
          <img src={absUrl(cfg.image)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C1117] via-[#0C1117]/40 to-transparent" />
        <button
          type="button"
          onClick={onBack}
          className="absolute z-10 top-3 left-3 inline-flex items-center gap-1 rounded-full bg-black/40 backdrop-blur-sm px-2.5 py-1 text-[11px] font-semibold hover:bg-black/60 transition-colors"
          data-testid="tour-back"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> All doors
        </button>
        {/* Dot navigation in hero */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="tour-dots">
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i === index ? "w-4 bg-[#F2CC8F]" : "w-1 bg-white/25"}`} />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-5 pb-5 -mt-4 relative flex-1 flex flex-col">
        {cfg.eyebrow && (
          <p className="text-[9px] font-bold uppercase tracking-[0.25em] mb-1" style={{ color: accent }}>{cfg.eyebrow}</p>
        )}
        <h3 className="font-['Playfair_Display'] text-xl leading-tight">{card.title}</h3>
        <p className="text-[13px] text-white/75 leading-relaxed mt-2">{renderLinked(card.quip)}</p>

        {cfg.body && (
          <p className="text-[12px] text-white/55 leading-relaxed mt-2">{renderLinked(cfg.body)}</p>
        )}

        {bullets.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {bullets.map((b) => (
              <li key={b} className="flex gap-2 text-[12px] text-white/70 leading-snug">
                <span className="mt-1.5 h-1 w-1 rounded-full shrink-0" style={{ backgroundColor: accent }} />
                <span>{renderLinked(b)}</span>
              </li>
            ))}
          </ul>
        )}

        {/* CTAs */}
        <div className="flex flex-wrap gap-2 mt-4">
          {cfg.cta_label && (
            <button
              type="button"
              onClick={() => onGo(cfg.cta_route)}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold text-[#0C1117] hover:brightness-110 transition-all"
              style={{ backgroundColor: accent }}
              data-testid="tour-cta"
            >
              {cfg.cta_label} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          {cfg.secondary_label && (
            <button
              type="button"
              onClick={() => onGo(cfg.secondary_route)}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border border-white/20 hover:bg-white/5 transition-colors"
              data-testid="tour-secondary-cta"
            >
              {cfg.secondary_label}
            </button>
          )}
        </div>

        {/* Prev/Next at bottom */}
        <div className="flex items-center justify-between mt-auto pt-4">
          <button type="button" onClick={onPrev} className="p-1.5 rounded-full hover:bg-white/10 text-white/40 hover:text-white transition-colors" aria-label="Previous" data-testid="tour-prev">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-[10px] text-white/25 font-medium">{index + 1} / {total}</span>
          <button type="button" onClick={onNext} className="p-1.5 rounded-full hover:bg-white/10 text-white/40 hover:text-white transition-colors" aria-label="Next" data-testid="tour-next">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
